/**
 * Очередь синхронизации.
 *
 * Операции копятся локально и отправляются, когда связь есть. Каждая несёт
 * client-generated syncId, поэтому повторная отправка безопасна
 * (ТЗ FR-SY-02, FR-SY-03). Синхронизация не блокирует тренировку (FR-SY-04).
 *
 * Очередь переживает перезагрузку страницы (FR-SY-06) — её хранит в IndexedDB
 * `adapters/web/src/storage/sync-queue-repository.ts`; операции гостя копятся до появления
 * аккаунта и уходят сразу после регистрации (FR-SY-07); отвергнутая по
 * содержимому покидает очередь, потому что повтор даст тот же ответ
 * (FR-SY-08).
 */

import type { DeliveryVerdict, FlushResult, QueuedOperation, Sender } from '../contracts/queue.js';
import { MAX_DELIVERY_ATTEMPTS } from '../contracts/queue.js';

export class SyncQueue {
  private items: QueuedOperation[] = [];

  /** Ставит операцию в очередь. Повтор с тем же syncId игнорируется. */
  enqueue(operation: QueuedOperation): void {
    if (this.items.some((item) => item.syncId === operation.syncId)) return;
    this.items.push(operation);
  }

  /** Текущее содержимое очереди в порядке постановки. */
  get pending(): readonly QueuedOperation[] {
    return this.items;
  }

  /** Пуста ли очередь. */
  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  /** Восстанавливает очередь из сохранённого состояния. */
  restore(operations: readonly QueuedOperation[]): void {
    this.items = [...operations];
  }

  /**
   * Пытается отправить всё накопленное.
   *
   * Разбор останавливается на первой операции, требующей повтора: если сервер
   * недоступен, дальнейшие попытки только потратят время и заряд батареи.
   * Порядок сохраняется — сессии приходят на сервер в том же порядке, в каком
   * их прошёл пользователь.
   */
  async flush(send: Sender): Promise<FlushResult> {
    let sent = 0;
    let dropped = 0;

    while (this.items.length > 0) {
      const next = this.items[0]!;
      const verdict = await this.deliver(send, next);

      if (verdict === 'retry') break;

      this.items.shift();
      if (verdict === 'delivered') sent += 1;
      else dropped += 1;
    }

    return { sent, dropped, remaining: this.items.length };
  }

  /** Отправляет операцию, учитывая исчерпание попыток. */
  private async deliver(send: Sender, operation: QueuedOperation): Promise<DeliveryVerdict> {
    const verdict = await send(operation);
    if (verdict !== 'retry') return verdict;

    const attempts = operation.attempts + 1;
    if (attempts >= MAX_DELIVERY_ATTEMPTS) return 'drop';

    this.items[0] = { ...operation, attempts };
    return 'retry';
  }
}
